import { _ as _define_property } from "@swc/helpers/_/_define_property";
import Decimal from 'decimal.js';
import { sendErrReport } from '~/infra/utils';
import { accountingFullName } from '~/integrations/Accounting/accounting-names';
import * as serviceBillingModel from '~/models/service-billing';
import * as serviceCostModel from '~/models/service-cost';
import { Accounting } from '~/types/accounting';
import { AccountingIntegrationType, ActivityLogAgreementState, ActivityLogBusinessDataType, ActivityLogEntryType, ActivityLogServiceState } from '~/types/activityLog';
import { MoneyModel } from './MoneyModel';
import { formatDateLong, formatDateLongOrdinal } from './time/selectors';
export * from '~/types/activityLog';
const INVOICE_NOTIFICATION_TYPES = new Set([
    'InvoiceIssued',
    'PaymentFailedCustomer',
    'InvoiceRefundedCustomer',
    'AdHocInvoiceIssued',
    'AdHocInvoiceOverdueReminderCustomer'
]);
export const isInvoiceNotification = (entry)=>INVOICE_NOTIFICATION_TYPES.has(entry.state);
const RECOGNIZED_ENTRY_TYPES = new Set(Object.values(ActivityLogEntryType));
const RECOGNIZED_BUSINESS_DATA_TYPES = new Set(Object.values(ActivityLogBusinessDataType));
const REMINDER_STATES = [
    ActivityLogAgreementState.SigningReminderSent,
    ActivityLogAgreementState.SigningReminderSentManual,
    ActivityLogAgreementState.MicroDepositReminderSent
];
const REFERENCE_FIELDS_STATES = [
    ActivityLogAgreementState.ReferenceFieldChanged,
    ActivityLogAgreementState.ReferenceFieldChangedInternal
];
const ACCOUNTING_BY_INTEGRATION_TYPE = {
    [AccountingIntegrationType.QBO]: Accounting.QBO,
    [AccountingIntegrationType.Xero]: Accounting.Xero
};
const formatNameAndEmail = (name, email)=>{
    if (!name) return '';
    return email ? "".concat(name, " (").concat(email, ")") : name;
};
const priceOf = (cost)=>cost ? serviceCostModel.price(cost) : null;
const requirePresent = (value, missing)=>{
    if (value == null) {
        throw new Error("Missing ".concat(missing, " data"));
    }
    return value;
};
export class ActivityLogEntryModel {
    static make(dto) {
        const entryType = dto.type;
        switch(dto.type){
            case ActivityLogEntryType.Relationship:
                return new ActivityLogAgreementEntryModel(dto);
            case ActivityLogEntryType.Charge:
                return new ActivityLogChargeEntryModel(dto);
            case ActivityLogEntryType.Credit:
                return new ActivityLogCreditEntryModel(dto);
            case ActivityLogEntryType.Invoice:
                return new ActivityLogInvoiceEntryModel(dto);
            case ActivityLogEntryType.Service:
                return new ActivityLogServiceEntryModel(dto);
            case ActivityLogEntryType.Business:
                return ActivityLogBusinessEntryModel.make(dto);
            case ActivityLogEntryType.SentNotification:
                return new ActivityLogSentNotificationEntryModel(dto);
            default:
                sendErrReport(new Error("Unknown entry type ".concat(entryType)));
                return new ActivityLogEntryModel(dto);
        }
    }
    isAgreementEntry() {
        return this.dto.type === ActivityLogEntryType.Relationship;
    }
    isServiceEntry() {
        return this.dto.type === ActivityLogEntryType.Service;
    }
    isChargeEntry() {
        return this.dto.type === ActivityLogEntryType.Charge;
    }
    isCreditEntry() {
        return this.dto.type === ActivityLogEntryType.Credit;
    }
    isInvoiceEntry() {
        return this.dto.type === ActivityLogEntryType.Invoice;
    }
    isBusinessEntry() {
        return this.dto.type === ActivityLogEntryType.Business;
    }
    isSentNotificationEntry() {
        return this.dto.type === ActivityLogEntryType.SentNotification;
    }
    isUnrecognizedEntry() {
        return !RECOGNIZED_ENTRY_TYPES.has(this.dto.type);
    }
    id() {
        return this.dto.id;
    }
    state() {
        return this.dto.state;
    }
    timestamp() {
        return this.timestampAsDate;
    }
    match(_visitor) {
        return null;
    }
    constructor(dto){
        _define_property(this, "dto", void 0);
        _define_property(this, "timestampAsDate", void 0);
        this.dto = dto;
        this.timestampAsDate = new Date(dto.timestamp);
    }
}
class ActivityLogPartyEntryModel extends ActivityLogEntryModel {
    customer() {
        return this.dto.data.customer;
    }
    vendor() {
        return this.dto.data.vendor;
    }
    customerName() {
        var _this_customer;
        return (_this_customer = this.customer()) === null || _this_customer === void 0 ? void 0 : _this_customer.name;
    }
    vendorName() {
        var _this_vendor;
        return (_this_vendor = this.vendor()) === null || _this_vendor === void 0 ? void 0 : _this_vendor.name;
    }
    customerNameAndEmail() {
        const customer = this.customer();
        return customer ? formatNameAndEmail(customer.name, customer.email) : '';
    }
    vendorNameAndEmail() {
        const vendor = this.vendor();
        return vendor ? formatNameAndEmail(vendor.name, vendor.email) : '';
    }
}
export class ActivityLogAgreementEntryModel extends ActivityLogPartyEntryModel {
    match(visitor) {
        return visitor.agreement(this);
    }
    partyType() {
        return this.dto.data.partyType;
    }
    referenceFieldsUpdated() {
        var _this_dto_data_referenceFieldsUpdated;
        return (_this_dto_data_referenceFieldsUpdated = this.dto.data.referenceFieldsUpdated) !== null && _this_dto_data_referenceFieldsUpdated !== void 0 ? _this_dto_data_referenceFieldsUpdated : [];
    }
    isReminderEntry() {
        return REMINDER_STATES.includes(this.state());
    }
    isDeletionEntry() {
        return this.state() === ActivityLogAgreementState.AmendmentDraftDiscarded;
    }
    isSaveEntry() {
        return this.state() === ActivityLogAgreementState.AmendmentDraftSaved;
    }
    isReferenceFieldsEntry() {
        return REFERENCE_FIELDS_STATES.includes(this.state());
    }
    channel() {
        return this.dto.data.channel;
    }
    paymentMethod() {
        return this.dto.data.paymentMethod;
    }
    hasReviewers() {
        return this.reviewers().length > 0;
    }
    reviewerNamesAndEmails() {
        return this.reviewers().map((reviewer)=>formatNameAndEmail(reviewer.name, reviewer.email));
    }
    change(key) {
        var _ref;
        var _this_dto_data_changes;
        return (_ref = (_this_dto_data_changes = this.dto.data.changes) === null || _this_dto_data_changes === void 0 ? void 0 : _this_dto_data_changes[key]) !== null && _ref !== void 0 ? _ref : null;
    }
    integrationName() {
        return this.dto.data.integrationName;
    }
    integrationServices() {
        return this.dto.data.integrationServices;
    }
    reviewers() {
        var _this_dto_data_reviewers;
        return (_this_dto_data_reviewers = this.dto.data.reviewers) !== null && _this_dto_data_reviewers !== void 0 ? _this_dto_data_reviewers : [];
    }
}
export class ActivityLogServiceEntryModel extends ActivityLogPartyEntryModel {
    match(visitor) {
        return visitor.service(this);
    }
    name() {
        return this.dto.data.service.name;
    }
    note() {
        return this.dto.data.service.note;
    }
    hasChanges() {
        return !!this.dto.data.service.changes;
    }
    descriptionChange() {
        var _this_dto_data_service_changes;
        return (_this_dto_data_service_changes = this.dto.data.service.changes) === null || _this_dto_data_service_changes === void 0 ? void 0 : _this_dto_data_service_changes.description;
    }
    termsChange() {
        var _this_dto_data_service_changes;
        return (_this_dto_data_service_changes = this.dto.data.service.changes) === null || _this_dto_data_service_changes === void 0 ? void 0 : _this_dto_data_service_changes.terms;
    }
    taxableChange() {
        var _ref;
        var _this_dto_data_service_changes;
        return (_ref = (_this_dto_data_service_changes = this.dto.data.service.changes) === null || _this_dto_data_service_changes === void 0 ? void 0 : _this_dto_data_service_changes.taxable) !== null && _ref !== void 0 ? _ref : null;
    }
    serviceChangesCost() {
        return this.changesCost;
    }
    billingChange() {
        return this.changesBilling;
    }
    priceChange() {
        return priceOf(this.changesCost);
    }
    priceBeforeChange() {
        return priceOf(this.beforeChangesCost);
    }
    integratedClassNameChange() {
        var _this_dto_data_service_changes_integratedClass, _this_dto_data_service_changes;
        return ((_this_dto_data_service_changes = this.dto.data.service.changes) === null || _this_dto_data_service_changes === void 0 ? void 0 : (_this_dto_data_service_changes_integratedClass = _this_dto_data_service_changes.integratedClass) === null || _this_dto_data_service_changes_integratedClass === void 0 ? void 0 : _this_dto_data_service_changes_integratedClass.name) || null;
    }
    integratedClassNameBeforeChange() {
        var _this_dto_data_service_beforeChanges_integratedClass, _this_dto_data_service_beforeChanges;
        return ((_this_dto_data_service_beforeChanges = this.dto.data.service.beforeChanges) === null || _this_dto_data_service_beforeChanges === void 0 ? void 0 : (_this_dto_data_service_beforeChanges_integratedClass = _this_dto_data_service_beforeChanges.integratedClass) === null || _this_dto_data_service_beforeChanges_integratedClass === void 0 ? void 0 : _this_dto_data_service_beforeChanges_integratedClass.name) || null;
    }
    isDiscountExpirationEntry() {
        return this.state() === ActivityLogServiceState.ServiceDiscountExpired;
    }
    constructor(dto){
        super(dto), _define_property(this, "changesCost", void 0), _define_property(this, "changesBilling", void 0), _define_property(this, "beforeChangesCost", void 0);
        const { changes, beforeChanges } = dto.data.service;
        this.changesCost = (changes === null || changes === void 0 ? void 0 : changes.cost) ? serviceCostModel.fromDTO(changes.cost) : undefined;
        this.changesBilling = (changes === null || changes === void 0 ? void 0 : changes.billing) ? serviceBillingModel.fromDTO(changes.billing) || undefined : undefined;
        this.beforeChangesCost = (beforeChanges === null || beforeChanges === void 0 ? void 0 : beforeChanges.cost) ? serviceCostModel.fromDTO(beforeChanges.cost) : undefined;
    }
}
export class ActivityLogChargeEntryModel extends ActivityLogPartyEntryModel {
    match(visitor) {
        return visitor.charge(this);
    }
    name() {
        return this.dto.data.charge.name;
    }
    note() {
        return this.dto.data.charge.note;
    }
    formattedPrice() {
        return this.price.display();
    }
    constructor(dto){
        super(dto), _define_property(this, "price", void 0);
        this.price = MoneyModel.fromDTO(dto.data.charge.temp_price);
    }
}
export class ActivityLogCreditEntryModel extends ActivityLogPartyEntryModel {
    match(visitor) {
        return visitor.credit(this);
    }
    name() {
        return this.dto.data.credit.name;
    }
    note() {
        return this.dto.data.credit.note;
    }
    formattedAmount() {
        return this.amount.display();
    }
    constructor(dto){
        super(dto), _define_property(this, "amount", void 0);
        this.amount = MoneyModel.fromDTO(dto.data.credit.amount);
    }
}
export class ActivityLogInvoiceEntryModel extends ActivityLogPartyEntryModel {
    match(visitor) {
        return visitor.invoice(this);
    }
    invoiceId() {
        return this.dto.data.invoice.id;
    }
    formattedNumber() {
        return this.dto.data.invoice.formattedNumber;
    }
    totalAmount() {
        return this.totalAmountAsMoney;
    }
    vendorBusinessName() {
        var _ref;
        var _this_vendor;
        return (_ref = (_this_vendor = this.vendor()) === null || _this_vendor === void 0 ? void 0 : _this_vendor.businessName) !== null && _ref !== void 0 ? _ref : '';
    }
    integrationType() {
        const { integrationName } = this.dto.data;
        return integrationName ? ACCOUNTING_BY_INTEGRATION_TYPE[integrationName] : undefined;
    }
    integrationFullName() {
        const integrationType = this.integrationType();
        return integrationType && accountingFullName[integrationType];
    }
    disputeWonDate() {
        return formatDateLong(this.timestamp());
    }
    paymentStoppedReason() {
        var _this_dto_data_paymentStopped;
        return (_this_dto_data_paymentStopped = this.dto.data.paymentStopped) === null || _this_dto_data_paymentStopped === void 0 ? void 0 : _this_dto_data_paymentStopped.reason;
    }
    refundRequestedReason() {
        var _this_dto_data_refundRequested, _this_dto_data_refunded;
        return ((_this_dto_data_refundRequested = this.dto.data.refundRequested) === null || _this_dto_data_refundRequested === void 0 ? void 0 : _this_dto_data_refundRequested.reason) || ((_this_dto_data_refunded = this.dto.data.refunded) === null || _this_dto_data_refunded === void 0 ? void 0 : _this_dto_data_refunded.reason);
    }
    totalRefundedAmount() {
        var _this_dto_data_refunded_amounts, _this_dto_data_refunded;
        return new MoneyModel(new Decimal(((_this_dto_data_refunded = this.dto.data.refunded) === null || _this_dto_data_refunded === void 0 ? void 0 : (_this_dto_data_refunded_amounts = _this_dto_data_refunded.amounts) === null || _this_dto_data_refunded_amounts === void 0 ? void 0 : _this_dto_data_refunded_amounts.totalRefundedAmount) || 0).div(100));
    }
    haveFees() {
        var _this_dto_data_refunded_amounts, _this_dto_data_refunded;
        return ((_this_dto_data_refunded = this.dto.data.refunded) === null || _this_dto_data_refunded === void 0 ? void 0 : (_this_dto_data_refunded_amounts = _this_dto_data_refunded.amounts) === null || _this_dto_data_refunded_amounts === void 0 ? void 0 : _this_dto_data_refunded_amounts.refundedFeesAmount) || 0 > 0;
    }
    isFullRefund() {
        var _this_dto_data_refunded;
        const amounts = (_this_dto_data_refunded = this.dto.data.refunded) === null || _this_dto_data_refunded === void 0 ? void 0 : _this_dto_data_refunded.amounts;
        // before partial refund was introduced, we had a single refund amount which we took from the invoice total amount
        if (!amounts) return true;
        return new Decimal(amounts.refundedAmount).div(100).eq(this.totalAmount().amount());
    }
    attachmentsAddedInfo() {
        const { displayNames, updatedBy } = requirePresent(this.dto.data.invoiceAttachmentsAdded, 'attachments added');
        return {
            displayNames,
            userName: updatedBy.userName,
            userEmail: updatedBy.userEmail
        };
    }
    attachmentRemovedInfo() {
        const { displayName, updatedBy } = requirePresent(this.dto.data.invoiceAttachmentRemoved, 'attachment removed');
        return {
            displayName,
            userName: updatedBy.userName,
            userEmail: updatedBy.userEmail
        };
    }
    adHocNotifyType() {
        return requirePresent(this.dto.data.adHocIssued, 'adHocIssued').notifyType;
    }
    adHocScheduledDate() {
        return formatDateLongOrdinal(requirePresent(this.dto.data.adHocScheduled, 'adHocScheduled').scheduledDate);
    }
    adHocLegalTermsDocument() {
        var _this_dto_data_adHocApproved;
        return (_this_dto_data_adHocApproved = this.dto.data.adHocApproved) === null || _this_dto_data_adHocApproved === void 0 ? void 0 : _this_dto_data_adHocApproved.legalTermsDocument;
    }
    adHocSharedByLinkInfo() {
        const { vendorName, vendorEmail } = requirePresent(this.dto.data.adHocSharedByLink, 'adHocSharedByLink');
        return {
            vendorName,
            vendorEmail
        };
    }
    constructor(dto){
        super(dto), _define_property(this, "totalAmountAsMoney", void 0);
        this.totalAmountAsMoney = MoneyModel.fromDTO(dto.data.invoice.totalAmount);
    }
}
export class ActivityLogSentNotificationEntryModel extends ActivityLogEntryModel {
    match(visitor) {
        return visitor.sentNotification(this);
    }
    data() {
        return this.dto.data;
    }
    isInvoiceKind() {
        return isInvoiceNotification(this.dto);
    }
    invoiceData() {
        return isInvoiceNotification(this.dto) ? this.dto.data : null;
    }
    invoiceId() {
        var _ref;
        var _this_invoiceData;
        return (_ref = (_this_invoiceData = this.invoiceData()) === null || _this_invoiceData === void 0 ? void 0 : _this_invoiceData.invoiceId) !== null && _ref !== void 0 ? _ref : null;
    }
    invoiceNumber() {
        var _ref;
        var _this_invoiceData;
        return (_ref = (_this_invoiceData = this.invoiceData()) === null || _this_invoiceData === void 0 ? void 0 : _this_invoiceData.invoiceNumber) !== null && _ref !== void 0 ? _ref : null;
    }
    firstDelivery() {
        return this.dto.data.deliveries[0];
    }
    deliveries() {
        return this.dto.data.deliveries;
    }
}
export class ActivityLogBusinessEntryModel extends ActivityLogEntryModel {
    static make(dto) {
        const model = new ActivityLogBusinessEntryModel(dto);
        if (model.isUnrecognizedBusinessEntry()) {
            sendErrReport(new Error("Unknown business entry type ".concat(model.dataType())));
        }
        return model;
    }
    match(visitor) {
        return visitor.business(this);
    }
    matchBusinessEntry(visitor) {
        if (this.isUnrecognizedBusinessEntry()) return null;
        const handle = visitor[this.dataType()];
        return handle(this.dto.data.value);
    }
    isBusinessNameChanged() {
        return this.dataType() === ActivityLogBusinessDataType.BusinessNameChanged || this.dataType() === ActivityLogBusinessDataType.CustomerNameChangedBySeller;
    }
    isCustomerAddressChangedEntry() {
        return this.dataType() === ActivityLogBusinessDataType.CustomerAddressChanged;
    }
    isUnrecognizedBusinessEntry() {
        return !RECOGNIZED_BUSINESS_DATA_TYPES.has(this.dataType());
    }
    dataType() {
        return this.dto.data.type;
    }
}
